import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Clock, 
  BookOpen, 
  Search,
  Filter,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  BarChart3,
  Lock,
  LogIn,
  Zap,
  Infinity,
  GraduationCap,
  AlertTriangle,
  X,
  RotateCcw,
  CheckCircle2,
  Trophy,
  Shield,
  CreditCard,
  Loader2,
  FolderOpen,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Colorful brain logo
const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

export default function QuizBrowser() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPremium, isAdmin, loading: authLoading, authChecked, logout } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedDifficulty, setSelectedDifficulty] = useState("all");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [userAttempts, setUserAttempts] = useState({});
  const [adminVerified, setAdminVerified] = useState(false);
  const [expandedTaskLists, setExpandedTaskLists] = useState({
    'task-list-a': false,
    'task-list-b': false,
    'task-list-c': false,
    'task-list-d': false,
    'task-list-e': false,
    'task-list-f': false,
    'task-list-g': false,
    'task-list-h': false,
    'task-list-i': false
  });

  // Verify admin status with API as backup
  useEffect(() => {
    const verifyAdmin = async () => {
      if (isAdmin) {
        setAdminVerified(true);
        return;
      }
      // Fallback: check via API if context doesn't have it yet
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const response = await axios.get(`${API}/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.is_admin) {
          setAdminVerified(true);
        }
      } catch (error) {
        // Not admin or error - that's fine
      }
    };
    if (isAuthenticated) {
      verifyAdmin();
    }
  }, [isAuthenticated, isAdmin]);

  // Show admin button if either context says admin OR API verified
  const showAdminButton = isAdmin || adminVerified;

  // Get completion status for a quiz
  const getQuizCompletion = (quizId) => {
    const attempts = userAttempts[quizId] || [];
    if (attempts.length === 0) return null;
    
    // Get best attempt
    const bestAttempt = attempts.reduce((best, current) => 
      current.percentage > best.percentage ? current : best
    , attempts[0]);
    
    return {
      attempts: attempts.length,
      bestScore: bestAttempt.percentage,
      lastAttempt: attempts[0] // Most recent
    };
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/stripe/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription cancelled successfully");
      setShowCancelModal(false);
      // Logout and redirect to home after cancellation
      logout();
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/stripe/customer-portal`, {
        return_url: window.location.href
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portal_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open subscription portal");
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzes();
    fetchCategories();
    fetchUserAttempts();
  }, []);

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/quizzes`);
      setQuizzes(response.data);
    } catch (error) {
      toast.error("Failed to load quizzes");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUserAttempts = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const response = await axios.get(`${API}/attempts?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Group attempts by quiz_id
      const grouped = {};
      response.data.forEach(attempt => {
        if (!grouped[attempt.quiz_id]) {
          grouped[attempt.quiz_id] = [];
        }
        grouped[attempt.quiz_id].push(attempt);
      });
      
      setUserAttempts(grouped);
    } catch (error) {
      console.error("Failed to fetch attempts:", error);
    }
  };

  const filteredQuizzes = quizzes.filter(quiz => {
    const matchesSearch = quiz.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quiz.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || quiz.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === "all" || quiz.difficulty === selectedDifficulty;
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  // Task List grouping configuration
  const taskListGroups = [
    {
      id: 'task-list-a',
      label: 'Task List A. Behaviorism and Philosophical Foundations',
      prefix: 'task-list-a',
      color: 'from-indigo-500 to-violet-600'
    },
    {
      id: 'task-list-b',
      label: 'Task List B. Concepts and Principles',
      prefix: 'task-list-b',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      id: 'task-list-c',
      label: 'Task List C. Measurement',
      prefix: 'task-list-c',
      color: 'from-sky-500 to-blue-600'
    },
    {
      id: 'task-list-d',
      label: 'Task List D. Experimental Design',
      prefix: 'task-list-d',
      color: 'from-amber-500 to-orange-600'
    },
    {
      id: 'task-list-e',
      label: 'Task List E. Ethical and Professional Issues',
      prefix: 'task-list-e',
      color: 'from-rose-500 to-pink-600'
    },
    {
      id: 'task-list-f',
      label: 'Task List F. Behavior Assessment',
      prefix: 'task-list-f',
      color: 'from-cyan-500 to-sky-600'
    },
    {
      id: 'task-list-g',
      label: 'Task List G. Behavior-Change Procedures',
      prefix: 'task-list-g',
      color: 'from-purple-500 to-fuchsia-600'
    },
    {
      id: 'task-list-h',
      label: 'Task List H. Selecting and Implementing Interventions',
      prefix: 'task-list-h',
      color: 'from-lime-500 to-green-600'
    },
    {
      id: 'task-list-i',
      label: 'Task List I. Personnel Supervision and Management',
      prefix: 'task-list-i',
      color: 'from-red-500 to-rose-600'
    }
  ];

  // Separate grouped quizzes from regular quizzes
  const getGroupedQuizzes = (prefix) => {
    return filteredQuizzes.filter(quiz => quiz.id.startsWith(prefix));
  };

  const getRegularQuizzes = () => {
    const allPrefixes = taskListGroups.map(g => g.prefix);
    return filteredQuizzes.filter(quiz => !allPrefixes.some(prefix => quiz.id.startsWith(prefix)));
  };

  const toggleTaskList = (taskListId) => {
    setExpandedTaskLists(prev => ({
      ...prev,
      [taskListId]: !prev[taskListId]
    }));
  };

  // Get count of quizzes in a task list group
  const getTaskListQuizCount = (prefix) => {
    return getGroupedQuizzes(prefix).length;
  };

  // Get completion stats for a task list group
  const getTaskListProgress = (prefix) => {
    const groupQuizzes = getGroupedQuizzes(prefix);
    const totalQuizzes = groupQuizzes.length;
    let completedQuizzes = 0;
    let totalBestScore = 0;
    
    groupQuizzes.forEach(quiz => {
      const completion = getQuizCompletion(quiz.id);
      if (completion) {
        completedQuizzes++;
        totalBestScore += completion.bestScore;
      }
    });
    
    const avgScore = completedQuizzes > 0 ? Math.round(totalBestScore / completedQuizzes) : 0;
    const progressPercent = totalQuizzes > 0 ? Math.round((completedQuizzes / totalQuizzes) * 100) : 0;
    
    return {
      completed: completedQuizzes,
      total: totalQuizzes,
      avgScore,
      progressPercent
    };
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Measurement": "bg-sky-500",
      "Behavior Change": "bg-teal-500",
      "Ethics": "bg-amber-500",
      "Assessment": "bg-rose-400",
      "Verbal Behavior": "bg-cyan-500",
      "Experimental Design": "bg-indigo-400",
      "Philosophical Foundations": "bg-slate-500",
      "Motivating Operations": "bg-emerald-500",
      "Stimulus Relations": "bg-purple-400",
      "Stimulus Control": "bg-blue-400",
      "Mock Exam": "bg-cyan-600"
    };
    return colors[category] || "bg-slate-500";
  };

  const getDifficultyStyle = (difficulty) => {
    const styles = {
      "easy": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
      "medium": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
      "hard": "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
    };
    return styles[difficulty] || "";
  };

  // Show login required screen if not authenticated or not premium
  // Wait for auth check to complete before showing login screen
  if (!authChecked || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }
  
  if (!isAuthenticated || !isPremium) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center p-6">
        <motion.div 
          className="max-w-md w-full text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-8 shadow-lg">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
              <Lock className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              {isAuthenticated ? "Subscription Required" : "Login Required"}
            </h1>
            <p className="text-slate-600 mb-6">
              {isAuthenticated 
                ? "Subscribe to access all quizzes and mock exams for your BCBA certification prep."
                : "Create an account and subscribe to access quizzes and track your progress toward BCBA certification."
              }
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate("/auth")}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                data-testid="login-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                {isAuthenticated ? "Subscribe Now - $7.00/month" : "Sign In / Create Account"}
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full text-slate-600"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/")}
                data-testid="back-btn"
                className="text-slate-600 hover:text-teal-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl overflow-hidden">
                  <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
                </div>
                <span className="font-bold text-xl tracking-tight text-slate-800 dark:text-slate-100">ABA Prep Lab</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {showAdminButton && (
                <Button 
                  variant="outline"
                  onClick={() => navigate("/admin")}
                  data-testid="admin-btn"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-600 dark:text-violet-300"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              )}
              <Button 
                variant="outline"
                onClick={() => navigate("/dictionary")}
                data-testid="dictionary-btn"
                className="border-slate-300 text-slate-700 hover:text-teal-600 dark:border-slate-600 dark:text-slate-300"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Dictionary
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/analytics")}
                data-testid="analytics-btn"
                className="border-slate-300 text-slate-700 hover:text-teal-600 dark:border-slate-600 dark:text-slate-300"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Adaptive Quiz Banner */}
        <motion.div
          className="mb-8 bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl p-6 text-white"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Adaptive Quiz Mode</h2>
                <p className="text-white/80 text-sm">Questions adjust to your skill level in real-time</p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/adaptive-quiz")}
              className="bg-white text-violet-600 hover:bg-white/90 font-semibold"
              data-testid="adaptive-quiz-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Start Adaptive Quiz
            </Button>
          </div>
        </motion.div>

        {/* Flashcard Review Banner */}
        <motion.div
          className="mb-8 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-2xl p-6 text-white"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">ABA Flashcards</h2>
                <p className="text-white/80 text-sm">400 terms with spaced repetition & APA citations</p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/flashcards")}
              className="bg-white text-teal-600 hover:bg-white/90 font-semibold"
              data-testid="flashcards-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Start Flashcards
            </Button>
          </div>
        </motion.div>

        {/* Practice Exam Generator Banner */}
        {isPremium && (
          <motion.div
            className="mb-8 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-6 text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Practice Exam Generator</h2>
                  <p className="text-white/80 text-sm">Create custom exams weighted toward your weak areas</p>
                </div>
              </div>
              <Button
                onClick={() => navigate("/practice-exam")}
                className="bg-white text-amber-600 hover:bg-white/90 font-semibold"
                data-testid="practice-exam-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Practice Exam
              </Button>
            </div>
          </motion.div>
        )}

        {/* Page Title */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h1 className="text-3xl font-bold mb-2" data-testid="page-title">Practice Quizzes</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Choose a quiz to test your BCBA knowledge
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div 
          className="flex flex-col md:flex-row gap-4 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search quizzes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-3">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
              <SelectTrigger className="w-[150px]" data-testid="difficulty-filter">
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Quiz Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 skeleton rounded-2xl" />
            ))}
          </div>
        ) : filteredQuizzes.length === 0 ? (
          <motion.div 
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <BookOpen className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No quizzes found</h3>
            <p className="text-slate-500">Try adjusting your filters</p>
          </motion.div>
        ) : (
          <div className="space-y-8">
            {/* Task List Groups */}
            {taskListGroups.map((group) => {
              const groupQuizzes = getGroupedQuizzes(group.prefix);
              if (groupQuizzes.length === 0) return null;
              
              const progress = getTaskListProgress(group.prefix);
              
              return (
                <motion.div 
                  key={group.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  {/* Task List Accordion Header */}
                  <button
                    onClick={() => toggleTaskList(group.id)}
                    className={`w-full bg-gradient-to-r ${group.color} rounded-xl p-4 text-left text-white shadow-lg hover:shadow-xl transition-all duration-200`}
                    data-testid={`${group.id}-toggle`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                          <FolderOpen className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{group.label}</h3>
                          <p className="text-white/80 text-sm">{groupQuizzes.length} quizzes</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Progress Indicator */}
                        {progress.completed > 0 && (
                          <div className="flex items-center gap-3 bg-white/20 rounded-lg px-3 py-2">
                            <div className="text-right">
                              <div className="flex items-center gap-1.5 text-sm font-semibold">
                                <CheckCircle2 className="w-4 h-4" />
                                <span>{progress.completed}/{progress.total}</span>
                              </div>
                              <div className="text-xs text-white/80">
                                Avg: {progress.avgScore}%
                              </div>
                            </div>
                            {/* Progress Bar */}
                            <div className="w-16 h-2 bg-white/30 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${
                                  progress.progressPercent === 100 
                                    ? 'bg-green-300' 
                                    : 'bg-white'
                                }`}
                                style={{ width: `${progress.progressPercent}%` }}
                              />
                            </div>
                          </div>
                        )}
                        <motion.div
                          animate={{ rotate: expandedTaskLists[group.id] ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="w-6 h-6" />
                        </motion.div>
                      </div>
                    </div>
                  </button>
                  
                  {/* Task List Quizzes */}
                  <AnimatePresence>
                    {expandedTaskLists[group.id] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4 pt-4 border-t border-slate-200/50 dark:border-slate-700/50">
                          {groupQuizzes.map((quiz, index) => {
                            const completion = getQuizCompletion(quiz.id);
                            return (
                              <motion.div
                                key={quiz.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="quiz-card bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
                                data-testid={`quiz-card-${quiz.id}`}
                              >
                                <div className={`h-1.5 ${getCategoryColor(quiz.category)}`} />
                                <div className="p-6">
                                  <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                      <Badge className={getDifficultyStyle(quiz.difficulty)}>
                                        {quiz.difficulty}
                                      </Badge>
                                      {completion && (
                                        <Badge className={`${
                                          completion.bestScore >= 80 
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                            : completion.bestScore >= 60 
                                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                                        }`}>
                                          {completion.bestScore >= 80 ? (
                                            <CheckCircle2 className="w-3 h-3 mr-1" />
                                          ) : (
                                            <Trophy className="w-3 h-3 mr-1" />
                                          )}
                                          {completion.bestScore}%
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600">
                                      <Clock className="w-4 h-4" />
                                      {quiz.time_limit} min
                                    </div>
                                  </div>
                                  <h3 className="font-bold text-lg mb-2 text-slate-900 dark:text-slate-100" data-testid={`quiz-title-${quiz.id}`}>
                                    {quiz.title}
                                  </h3>
                                  <p className="text-sm text-slate-700 dark:text-slate-300 mb-4 line-clamp-2">
                                    {quiz.description}
                                  </p>
                                  {completion && (
                                    <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                      <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 mb-2">
                                        <span>Best Score: <strong className="text-slate-900 dark:text-slate-100">{completion.bestScore}%</strong></span>
                                        <span>{completion.attempts} attempt{completion.attempts !== 1 ? 's' : ''}</span>
                                      </div>
                                      <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                                        <div 
                                          className={`h-2 rounded-full transition-all ${
                                            completion.bestScore >= 80 ? 'bg-green-500' : completion.bestScore >= 60 ? 'bg-amber-500' : 'bg-slate-400'
                                          }`}
                                          style={{ width: `${completion.bestScore}%` }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-2 h-2 rounded-full ${getCategoryColor(quiz.category)}`} />
                                      <span className="text-sm font-medium text-slate-600">{quiz.category}</span>
                                    </div>
                                    <span className="text-sm font-medium text-slate-600">{quiz.question_count} questions</span>
                                  </div>
                                  {completion ? (
                                    <div className="mt-4 space-y-2">
                                      <div className="flex gap-2">
                                        <Button 
                                          className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-semibold"
                                          onClick={() => navigate(`/quiz/${quiz.id}`)}
                                          data-testid={`redo-timed-${quiz.id}`}
                                        >
                                          <RotateCcw className="w-4 h-4 mr-1" />
                                          Redo Timed
                                        </Button>
                                        <Button 
                                          variant="outline"
                                          className="flex-1 border-violet-300 text-violet-600 hover:bg-violet-50 font-semibold"
                                          onClick={() => navigate(`/quiz/${quiz.id}?study=true`)}
                                          data-testid={`redo-study-${quiz.id}`}
                                        >
                                          <RotateCcw className="w-4 h-4 mr-1" />
                                          Redo Study
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex gap-2 mt-4">
                                      <Button 
                                        className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-semibold"
                                        onClick={() => navigate(`/quiz/${quiz.id}`)}
                                        data-testid={`start-quiz-${quiz.id}`}
                                      >
                                        <Clock className="w-4 h-4 mr-1" />
                                        Timed
                                      </Button>
                                      <Button 
                                        variant="outline"
                                        className="flex-1 border-violet-300 text-violet-600 hover:bg-violet-50 font-semibold"
                                        onClick={() => navigate(`/quiz/${quiz.id}?study=true`)}
                                        data-testid={`study-mode-${quiz.id}`}
                                      >
                                        <Infinity className="w-4 h-4 mr-1" />
                                        Study
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}

            {/* Regular Quizzes (not in task lists) */}
            {getRegularQuizzes().length > 0 && (
              <>
                {taskListGroups.some(g => getGroupedQuizzes(g.prefix).length > 0) && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4"
                  >
                    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Other Quizzes</h2>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Additional practice quizzes and mock exams</p>
                  </motion.div>
                )}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {getRegularQuizzes().map((quiz, index) => {
                    const completion = getQuizCompletion(quiz.id);
                    return (
                      <motion.div
                        key={quiz.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="quiz-card bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
                        data-testid={`quiz-card-${quiz.id}`}
                      >
                        <div className={`h-1.5 ${getCategoryColor(quiz.category)}`} />
                        <div className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <Badge className={getDifficultyStyle(quiz.difficulty)}>
                                {quiz.difficulty}
                              </Badge>
                              {completion && (
                                <Badge className={`${
                                  completion.bestScore >= 80 
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                    : completion.bestScore >= 60 
                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                                }`}>
                                  {completion.bestScore >= 80 ? (
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                  ) : (
                                    <Trophy className="w-3 h-3 mr-1" />
                                  )}
                                  {completion.bestScore}%
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600">
                              <Clock className="w-4 h-4" />
                              {quiz.time_limit} min
                            </div>
                          </div>
                          <h3 className="font-bold text-lg mb-2 text-slate-900 dark:text-slate-100" data-testid={`quiz-title-${quiz.id}`}>
                            {quiz.title}
                          </h3>
                          <p className="text-sm text-slate-700 dark:text-slate-300 mb-4 line-clamp-2">
                            {quiz.description}
                          </p>
                          {completion && (
                            <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                              <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 mb-2">
                                <span>Best Score: <strong className="text-slate-900 dark:text-slate-100">{completion.bestScore}%</strong></span>
                                <span>{completion.attempts} attempt{completion.attempts !== 1 ? 's' : ''}</span>
                              </div>
                              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full transition-all ${
                                    completion.bestScore >= 80 ? 'bg-green-500' : completion.bestScore >= 60 ? 'bg-amber-500' : 'bg-slate-400'
                                  }`}
                                  style={{ width: `${completion.bestScore}%` }}
                                />
                              </div>
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${getCategoryColor(quiz.category)}`} />
                              <span className="text-sm font-medium text-slate-600">{quiz.category}</span>
                            </div>
                            <span className="text-sm font-medium text-slate-600">{quiz.question_count} questions</span>
                          </div>
                          {completion ? (
                            <div className="mt-4 space-y-2">
                              <div className="flex gap-2">
                                <Button 
                                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-semibold"
                                  onClick={() => navigate(`/quiz/${quiz.id}`)}
                                  data-testid={`redo-timed-${quiz.id}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-1" />
                                  Redo Timed
                                </Button>
                                <Button 
                                  variant="outline"
                                  className="flex-1 border-violet-300 text-violet-600 hover:bg-violet-50 font-semibold"
                                  onClick={() => navigate(`/quiz/${quiz.id}?study=true`)}
                                  data-testid={`redo-study-${quiz.id}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-1" />
                                  Redo Study
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2 mt-4">
                              <Button 
                                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-semibold"
                                onClick={() => navigate(`/quiz/${quiz.id}`)}
                                data-testid={`start-quiz-${quiz.id}`}
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                Timed
                              </Button>
                              <Button 
                                variant="outline"
                                className="flex-1 border-violet-300 text-violet-600 hover:bg-violet-50 font-semibold"
                                onClick={() => navigate(`/quiz/${quiz.id}?study=true`)}
                                data-testid={`study-mode-${quiz.id}`}
                              >
                                <Infinity className="w-4 h-4 mr-1" />
                                Study
                              </Button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Subscription Management */}
        <div className="mt-12 flex items-center justify-between border-t border-slate-200 dark:border-slate-700 pt-6">
          <button
            onClick={handleManageSubscription}
            disabled={portalLoading}
            className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium transition-colors"
            data-testid="manage-subscription-btn"
          >
            {portalLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4" />
            )}
            {portalLoading ? "Opening Portal..." : "Manage Subscription"}
          </button>
        </div>
      </main>

      {/* Cancel Subscription Modal */}
      <AnimatePresence>
        {showCancelModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setShowCancelModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
              data-testid="cancel-modal"
            >
              <div className="bg-amber-500 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-white">Cancel Subscription</h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowCancelModal(false)}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              <div className="p-6">
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Are you sure you want to cancel your subscription? You will lose access to:
                </p>
                <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    All practice quizzes and mock exams
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    AI-powered explanations
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Study Mode and Adaptive Quizzes
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    ABA Prep Lab Dictionary
                  </li>
                </ul>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowCancelModal(false)}
                    className="flex-1"
                    data-testid="keep-subscription-btn"
                  >
                    Keep Subscription
                  </Button>
                  <Button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                    data-testid="confirm-cancel-btn"
                  >
                    {cancelLoading ? "Cancelling..." : "Yes, Cancel"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
